import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { apiClient } from "../api/client";
import type { CatalogEntry, DemoLaunchResponse } from "../api/types";

export function DemoLauncherPage(): ReactElement {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [entry, setEntry] = useState<CatalogEntry | null>(null);
  const [launch, setLaunch] = useState<DemoLaunchResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      try {
        const [entryResp, launchResp] = await Promise.all([
          apiClient.get<CatalogEntry>(`/api/catalog/${slug}`),
          apiClient.post<DemoLaunchResponse>("/api/demo/launch", { slug }),
        ]);
        setEntry(entryResp.data);
        setLaunch(launchResp.data);

        // Redirect mode — авто-редирект ТОЛЬКО если не нужны демо-доступы (A3).
        // Если у продукта есть demo_credentials (login-wall) — показываем их и
        // даём открыть demo вручную, чтобы проспект успел ввести логин/пароль.
        if (launchResp.data.mode === "redirect" && !entryResp.data.demo_credentials) {
          setTimeout(() => {
            window.location.href = launchResp.data.url;
          }, 1500); // даём пользователю увидеть loading
        }
      } catch (err: unknown) {
        const detail =
          (err as { response?: { data?: { detail?: string } } }).response?.data
            ?.detail ?? "Не удалось запустить demo";
        setError(detail);
      }
    })();
  }, [slug]);

  if (error) {
    // Day 03: graceful coming_soon UI вместо просто error
    const isComingSoon = error.toLowerCase().includes("разработке");
    return (
      <section className="demo-launcher demo-launcher--error">
        <div className="demo-launcher__error-icon">
          {isComingSoon ? "🚧" : "⚠️"}
        </div>
        <h1>{isComingSoon ? "Demo в разработке" : "Ошибка запуска demo"}</h1>
        <p className="demo-launcher__error-text">{error}</p>
        {isComingSoon && (
          <p className="demo-launcher__error-hint">
            Хотите получить ранний доступ? Свяжитесь с менеджером IT-Navigate
            или дождитесь следующей итерации.
          </p>
        )}
        <div className="demo-launcher__error-actions">
          <button
            onClick={() => navigate(`/product/${slug}`)}
            className="btn-secondary"
          >
            Подробнее о продукте
          </button>
          <button onClick={() => navigate("/catalog")} className="btn-primary">
            Вернуться в каталог
          </button>
        </div>
      </section>
    );
  }

  if (!entry || !launch) {
    return (
      <section className="demo-launcher demo-launcher--loading">
        <h1>Готовим demo…</h1>
        <p>Пожалуйста, подождите.</p>
      </section>
    );
  }

  if (launch.mode === "redirect") {
    // A3: login-wall продукт с демо-доступами — показываем креды + ручной запуск,
    // чтобы проспект увидел логин/пароль ДО перехода (иначе упёрся бы в форму входа).
    if (entry.demo_credentials) {
      return (
        <section
          className="demo-launcher demo-launcher--loading"
          style={{ maxWidth: 560, margin: "0 auto", textAlign: "center" }}
        >
          <h1>Демо «{entry.name}»</h1>
          <p>Для входа в демо используйте доступы ниже:</p>
          <div
            style={{
              margin: "16px 0",
              padding: 16,
              background: "#f0f4ff",
              border: "1px solid #b8c8f0",
              borderRadius: 10,
              textAlign: "left",
              fontSize: 14,
              lineHeight: 1.6,
            }}
          >
            🔑 {entry.demo_credentials}
          </div>
          <a
            href={launch.url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            style={{
              display: "inline-block",
              padding: "12px 24px",
              background: "#0b5fff",
              color: "white",
              borderRadius: 8,
              fontWeight: 600,
              textDecoration: "none",
            }}
          >
            Открыть демо →
          </a>
        </section>
      );
    }
    return (
      <section className="demo-launcher demo-launcher--loading">
        <h1>Запускаем {entry.name}…</h1>
        <p>
          Вас перенаправит на demo через несколько секунд. Если этого не
          произошло —{" "}
          <a href={launch.url}>перейдите вручную</a>.
        </p>
      </section>
    );
  }

  // iframe mode
  return (
    <section className="demo-launcher demo-launcher--iframe">
      <header className="demo-launcher__header">
        <button
          className="btn-secondary"
          onClick={() => navigate("/catalog")}
          type="button"
        >
          ← Вернуться в каталог
        </button>
        <div className="demo-launcher__title">
          <strong>{entry.name}</strong>
          <span className="demo-launcher__mode">demo в режиме просмотра</span>
        </div>
      </header>
      <iframe
        src={launch.url}
        title={`${entry.name} demo`}
        className="demo-launcher__iframe"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        referrerPolicy="no-referrer"
      />
    </section>
  );
}
